import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  Button,
  Skeleton,
  Menu,
  Checkbox,
  FormControlLabel,
  Link,
  Modal,
  Slide,
  IconButton,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel as MuiFormControlLabel,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails} from "@mui/material";
import {
  BugReport,
  CheckCircle,
  Close as MuiCloseIcon,
  ExpandMore,
  KeyboardArrowDown,
  Language,
  Storage,
  Warning,
} from "@mui/icons-material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import Chart from "react-apexcharts";
import { ReactComponent as FilterIcon } from "../../../../vendor/_new/icon/sites/filter.svg";
import { ReactComponent as ExternalLinkIcon } from "../../../../vendor/_new/icon/overview/action.svg";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import colors from "../../../../utils/colors";
import { apexVerticalAreaGradient, apexNoOfYAxisTitle } from "../../../../utils/apexChartDefaults";
import V2PageHeader from "../../../common/V2PageHeader";
import useAuth from "../../../../hooks/useAuth";
import useFindings from "../../../../hooks/user/useFindings";
import useHome from "../../../../hooks/user/useHome";
import RefreshIconButton from "../../../common/RefreshIconButton";
import ThreatInsightsPanel from "./ThreatInsightsPanel";
import DastActions from "./DastActions";
import { SafeTextField } from "../../../common/Greens";
import {
  isFindingsPostureRoute,
  isFindingsThreatInsightsRoute,
} from "./findingsPaths";

const headTypographyStyle = {
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: "medium",
  color: colors.neutral[1000],
};

const cellTypographyStyle = {
  fontSize: 14,
  lineHeight: "21px",
  color: colors.neutral[800],
};

const brandBlue = colors.primary[300];

const findingsFilterPillSx = (hasValue) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  height: 36,
  borderRadius: "8px",
  border: `1px solid ${hasValue ? brandBlue : colors.neutral[200]}`,
  backgroundColor: "#fff",
  boxSizing: "border-box",
  cursor: "pointer",
  minWidth: 140,
  "&:hover": {
    borderColor: brandBlue,
    "& .filter-label": {
      color: brandBlue,
    },
  },
});

const findingsFilterPillLabelSx = (hasValue) => ({
  fontSize: 14,
  color: hasValue ? brandBlue : colors.neutral[800],
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const findingsFilterMenuPaperSx = {
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
  mt: 1,
  overflow: "hidden",
  "& .MuiList-root": {
    py: 0,
    px: 0,
  },
};

// Finding evidence came in two shapes historically — the legacy per-scan
// alert returned `risk_definition.additional_info.Instances[]` with ZAP's
// own `uri`/`otherinfo`/`requestHeader`/`responseHeader` keys and a parsed
// `risk_definition.references[]` array; the modern scanner mapper (post
// enrichment) puts flat `instances[]` with `url`/`param`/`evidence` plus a
// single whitespace-separated `reference` string. The drawer uses whichever
// shape is populated.
function normalizeFindingEvidence(risk) {
  if (!risk) return { instances: [], references: [] };
  const riskDef = risk.riskDefinition || risk.risk_definition || {};
  const additionalInfo = riskDef.additional_info || riskDef.additionalInfo || {};

  // Instances: prefer the modern flat array (fresh scanner), fall back to
  // the legacy nested one.
  let instances = Array.isArray(risk.instances) ? risk.instances : [];
  if (!instances.length && Array.isArray(additionalInfo.Instances)) {
    instances = additionalInfo.Instances;
  }
  instances = instances.map((raw) => ({
    uri: raw.uri || raw.url || "",
    method: raw.method || "GET",
    param: raw.param || raw.parameter || "",
    evidence: raw.evidence || "",
    attack: raw.attack || "",
    confidence: raw.confidence || "",
    otherinfo: raw.otherinfo || raw.otherInfo || "",
    requestHeader: raw.requestHeader || raw.request_header || "",
    requestBody: raw.requestBody || raw.request_body || "",
    responseHeader: raw.responseHeader || raw.response_header || "",
    responseBody: raw.responseBody || raw.response_body || "",
  }));

  // References: prefer the structured array; otherwise parse the single
  // reference string (ZAP delimits with newlines or just spaces).
  let references = Array.isArray(riskDef.references) ? riskDef.references : [];
  if (!references.length) {
    const refStr = typeof risk.reference === "string" ? risk.reference : "";
    references = refStr
      .split(/[\s\n]+/)
      .map((s) => s.trim())
      .filter((s) => /^https?:\/\//i.test(s));
  }

  return { instances, references };
}

const getSeverityLabel = (severity) => {
  const severityLower = severity?.toLowerCase();
  let backgroundColor, borderColor, fontColor;

  switch (severityLower) {
    case "critical":
      backgroundColor = colors.red[10];
      borderColor = colors.red[100];
      fontColor = colors.red[200];
      break;
    case "high":
      backgroundColor = "#FFF0E6";
      borderColor = "#FF6B35";
      fontColor = "#FF6B35";
      break;
    case "medium":
      backgroundColor = "#FFF6D0";
      borderColor = colors.yellow[200];
      fontColor = "#A68705";
      break;
    case "low":
      backgroundColor = colors.neutral[10];
      borderColor = colors.neutral[400];
      fontColor = colors.neutral[700];
      break;
    default:
      backgroundColor = colors.neutral[10];
      borderColor = colors.neutral[400];
      fontColor = colors.neutral[700];
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        minWidth: 70,
        height: 25,
        bgcolor: backgroundColor,
        borderRadius: "20px",
        border: `1px solid ${borderColor}`,
        px: 2,
      }}
    >
      <Typography sx={ { fontSize: 12, lineHeight: "18px", color: fontColor, textTransform: "capitalize" }}>
        {severity}
      </Typography>
    </Stack>
  );
};

const getStatusLabel = (status) => {
  const isResolved = status?.toLowerCase() === "closed";
  const label = isResolved ? "Resolved" : "Unresolved";
  const backgroundColor = isResolved ? colors.green[10] : colors.neutral[10];
  const borderColor = isResolved ? colors.green[100] : colors.neutral[400];
  const fontColor = isResolved ? colors.green[300] : colors.neutral[700];

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        minWidth: 80,
        height: 25,
        bgcolor: backgroundColor,
        borderRadius: "20px",
        border: `1px solid ${borderColor}`,
        px: 2,
      }}
    >
      <Typography sx={ { fontSize: 12, lineHeight: "18px", color: fontColor }}>{label}</Typography>
    </Stack>
  );
};

const formatCount = (count) => {
  if (count >= 1000) {
    const k = Math.floor(count / 1000);
    return `${k}K`;
  }
  return count.toString();
};

const CountBadge = ({ count, type }) => {
  const badgeType = (type || "").toString().toUpperCase();
  const badgePalette = {
    C: { backgroundColor: colors.red[10], borderColor: colors.red[100], fontColor: colors.red[200] },
    H: { backgroundColor: "#FFF0E6", borderColor: "#FF6B35", fontColor: "#FF6B35" },
    M: { backgroundColor: "#FFF6D0", borderColor: colors.yellow[200], fontColor: "#A68705" },
    L: { backgroundColor: "#EAF7F0", borderColor: "#56B883", fontColor: "#178F4F" },
  };
  const { backgroundColor, borderColor, fontColor } = badgePalette[badgeType] || badgePalette.H;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.5,
        py: 0.5,
        bgcolor: backgroundColor,
        borderRadius: "12px",
        border: `1px solid ${borderColor}`,
      }}
    >
      <Typography sx={ { fontSize: 12, fontWeight: "medium", color: fontColor }}>
        {formatCount(count)} {badgeType || "H"}
      </Typography>
    </Stack>
  );
};


// Posture Findings and Threat Insights are separate routes under /v2/findings/*.
// Legacy /v2/findings?mode=threats redirects via LegacyFindingsRedirect.
const MODE_POSTURE = "posture";
const MODE_THREATS = "threats";

const NewFindingsPage = ({ forcedMode } = {}) => {
  const { isAuthenticated } = useAuth();
  const { curProjectId, projectList } = useHome();
  const location = useLocation();

  const mode = useMemo(() => {
    if (forcedMode === MODE_THREATS || forcedMode === MODE_POSTURE) {
      return forcedMode;
    }
    if (isFindingsThreatInsightsRoute(location.pathname)) {
      return MODE_THREATS;
    }
    if (isFindingsPostureRoute(location.pathname)) {
      return MODE_POSTURE;
    }
    try {
      const q = new URLSearchParams(location.search || "");
      return q.get("mode") === MODE_THREATS ? MODE_THREATS : MODE_POSTURE;
    } catch (_) {
      return MODE_POSTURE;
    }
  }, [forcedMode, location.pathname, location.search]);

  const pageTitle =
    mode === MODE_THREATS ? "Threat Insights" : "Posture Findings";

  const {
    risks,
    risksTotal,
    severityCounts,
    topResources,
    externalExposureTrend,
    riskDetails,
    loading,
    filters,
    setFilters,
    getRisks,
    getSeverityCounts,
    getTopResources,
    getExternalExposureTrend,
    getRiskDetails,
    acceptRisk,
    unacceptRisk,
    updateRiskStatus,
    updateFinding,
    stopScan,
    getScanReportUrl,
    createSuppressionRule,
  } = useFindings();

  const [severityAnchor, setSeverityAnchor] = useState(null);
  const [vulnerabilityAnchor, setVulnerabilityAnchor] = useState(null);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [resourcesAnchor, setResourcesAnchor] = useState(null);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [selectedVulnerability, setSelectedVulnerability] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRiskId, setSelectedRiskId] = useState(null);
  const [drawerTab, setDrawerTab] = useState(0);
  const [riskDetailsLoading, setRiskDetailsLoading] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [acceptAccordionOpen, setAcceptAccordionOpen] = useState(false);
  const [acceptReason, setAcceptReason] = useState("");
  const [acceptingRisk, setAcceptingRisk] = useState(false);
  const [unacceptingRisk, setUnacceptingRisk] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("OPEN");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [allRisks, setAllRisks] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const ITEMS_PER_PAGE = 100;

  const prevFiltersRef = useRef(filters);
  const prevProjectIdRef = useRef(curProjectId);
  const lastRisksLengthRef = useRef(0);

  useEffect(() => {
    setSelectedSeverity(filters.threat_level);
    setSelectedVulnerability(filters.cwe_id);
    setSelectedStatus(filters.status);
    setSelectedResource(filters.target_id);
    setSearchInput(filters.search || "");
  }, [filters]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshAllData = useCallback(() => {
    if (!isAuthenticated) return;
    setIsRefreshing(true);

    getSeverityCounts(curProjectId, "all", null);
    getTopResources(curProjectId, "all", 5, null);
    getExternalExposureTrend(curProjectId, "all", 7, null);
    setAllRisks([]);
    setHasMore(true);
    setIsLoadingMore(false);
    getRisks(curProjectId, "all", filters, ITEMS_PER_PAGE, 0, null);

    setTimeout(() => setIsRefreshing(false), 1000);
  }, [isAuthenticated, curProjectId, filters, getSeverityCounts, getTopResources, getExternalExposureTrend, getRisks]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    const projectChanged = prevProjectIdRef.current !== curProjectId;

    const controller = new AbortController();
    const signal = controller.signal;

    getSeverityCounts(curProjectId, "all", signal);
    getTopResources(curProjectId, "all", 5, signal);
    getExternalExposureTrend(curProjectId, "all", 7, signal);

    if (filtersChanged || projectChanged) {
      setAllRisks([]);
      setHasMore(true);
      setIsLoadingMore(false);
      prevFiltersRef.current = filters;
      prevProjectIdRef.current = curProjectId;
      getRisks(curProjectId, "all", filters, ITEMS_PER_PAGE, 0, signal);
    } else if (allRisks.length === 0) {
      getRisks(curProjectId, "all", filters, ITEMS_PER_PAGE, 0, signal);
    }

    return () => controller.abort();
  }, [isAuthenticated, curProjectId, filters, getSeverityCounts, getTopResources, getExternalExposureTrend, getRisks, allRisks.length]);

  useEffect(() => {
    if (!risks || !Array.isArray(risks)) return;

    if (isLoadingMore) {
      setAllRisks((prev) => {
        const prevIds = new Set(prev.map((r) => r.id).filter(Boolean));
        const newRisks = risks.filter((risk) => !prevIds.has(risk.id));
        if (newRisks.length > 0) {
          const updatedRisks = [...prev, ...newRisks];
          lastRisksLengthRef.current = risks.length;
          setHasMore(updatedRisks.length < risksTotal);
          setIsLoadingMore(false);
          return updatedRisks;
        }
        setHasMore(false);
        setIsLoadingMore(false);
        return prev;
      });
    } else {
      // Always replace with freshest server response for filter/search requests.
      setAllRisks(risks);
      lastRisksLengthRef.current = risks.length;
      setHasMore(risks.length < risksTotal);
    }
  }, [risks, risksTotal, isLoadingMore, filters]);

  useEffect(() => {
    if (!hasMore || isLoadingMore || allRisks.length < ITEMS_PER_PAGE) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const perfectScrollbar = scrollContainerRef.current?.querySelector?.(".ps") || document.querySelector(".ps");
    const root = perfectScrollbar || null;
    const rootMargin = "200px";

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          if (allRisks.length >= ITEMS_PER_PAGE && allRisks.length < risksTotal) {
            setIsLoadingMore(true);
            const nextOffset = allRisks.length;
            getRisks(curProjectId, "all", filters, ITEMS_PER_PAGE, nextOffset, null);
          }
        }
      },
      {
        root: root,
        rootMargin: rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, allRisks.length, risksTotal, curProjectId, filters, getRisks]);

  const handleSeverityFilterChange = (severity) => {
    const newSeverity = selectedSeverity === severity ? null : severity;
    setSelectedSeverity(newSeverity);
    setSeverityAnchor(null);
    setFilters({
      ...filters,
      threat_level: newSeverity,
    });
  };

  const handleVulnerabilityFilterChange = (cweId) => {
    const newCweId = selectedVulnerability === cweId ? null : cweId;
    setSelectedVulnerability(newCweId);
    setVulnerabilityAnchor(null);
    setFilters({
      ...filters,
      cwe_id: newCweId,
    });
  };

  const handleStatusFilterChange = (status) => {
    const newStatus = selectedStatus === status ? null : status;
    setSelectedStatus(newStatus);
    setStatusAnchor(null);
    setFilters({
      ...filters,
      status: newStatus,
    });
  };

  const handleResourceFilterChange = (resource) => {
    const newResource = selectedResource === resource ? null : resource;
    setSelectedResource(newResource);
    setResourcesAnchor(null);
    setFilters({
      ...filters,
      target_id: newResource,
    });
  };

  const handleSearchApply = () => {
    const normalizedSearch = searchInput?.trim() || null;
    setFilters({
      ...filters,
      search: normalizedSearch,
    });
  };

  const clearAllFilters = () => {
    setSelectedSeverity(null);
    setSelectedVulnerability(null);
    setSelectedStatus(null);
    setSelectedResource(null);
    setSearchInput("");
    setFilters({
      target_id: null,
      target_ids: null,
      threat_level: null,
      scan_type: null,
      status: null,
      accepted: null,
      cwe_id: null,
      cvss_min: null,
      cvss_max: null,
      search: null,
      first_detected_from: null,
      first_detected_to: null,
      last_detected_from: null,
      last_detected_to: null,
    });
  };

  const uniqueVulnerabilities = useMemo(() => {
    if (!allRisks || allRisks.length === 0) return [];
    const vulnerabilities = new Set();
    allRisks.forEach((risk) => {
      const cweId = risk.cwe_id || risk.riskDefinition?.additional_info?.["CWE Id"];
      if (cweId) {
        vulnerabilities.add(cweId);
      }
    });
    return Array.from(vulnerabilities).sort().slice(0, 20);
  }, [allRisks]);

  const uniqueStatuses = useMemo(() => {
    if (!allRisks || allRisks.length === 0) return [];
    return Array.from(new Set(allRisks.map((risk) => risk.status).filter(Boolean))).sort();
  }, [allRisks]);

  const uniqueResources = useMemo(() => {
    if (!allRisks || !Array.isArray(allRisks)) return [];
    const map = new Map();
    allRisks.forEach((risk) => {
      const id = risk.target_id || risk.targetId;
      const label = risk.targetLabel || risk.target || risk.target_id || risk.targetId;
      if (id && label && !map.has(id)) map.set(id, label);
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [allRisks]);

  const selectedResourceLabel = useMemo(() => {
    if (!selectedResource) return null;
    const match = uniqueResources.find((r) => r.id === selectedResource);
    return match?.label || selectedResource;
  }, [selectedResource, uniqueResources]);

  const totalVulnerabilities = useMemo(() => {
    if (!severityCounts) return 0;
    // Sum every severity bucket the backend returned. `critical` is optional
    // (vendor is ZAP-based, doesn't emit Critical) and defaults to 0.
    // Informational was previously excluded from this counter, which silently
    // dropped valid findings from the "N vulnerabilities" summary line —
    // now included so the counter matches the sum of every card body.
    return (
      (severityCounts.critical || 0) +
      (severityCounts.high || 0) +
      (severityCounts.medium || 0) +
      (severityCounts.low || 0) +
      (severityCounts.informational || 0)
    );
  }, [severityCounts]);

  // LE-433: empty findings → flat zero trend; resources still list targets
  // with coerced 0H/0M/0L (no "No data" copy).
  const findingsAreEmpty =
    (severityCounts !== null && totalVulnerabilities === 0) ||
    (!loading && Array.isArray(allRisks) && allRisks.length === 0 && totalVulnerabilities === 0);

  // Prefer a flat zero series over "No trend data" copy when empty.
  const exposureTrendChartData = useMemo(() => {
    const buildZeroDays = (numDays = 7) => {
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      const out = [];
      for (let i = numDays - 1; i >= 0; i--) {
        const d = new Date(today.getTime() - i * 86400000);
        out.push({ date: d.toISOString().slice(0, 10), total: 0 });
      }
      return out;
    };

    if (findingsAreEmpty) return buildZeroDays(7);
    if (!Array.isArray(externalExposureTrend)) return null;
    if (externalExposureTrend.length === 0) return buildZeroDays(7);
    // Defensive: never plot positive totals when severity says zero vulns.
    if (totalVulnerabilities === 0 && severityCounts !== null) {
      return buildZeroDays(
        Math.max(7, externalExposureTrend.length || 7)
      );
    }
    return externalExposureTrend.map((item) => ({
      date: item.date,
      total: Number(item.total) || 0,
    }));
  }, [findingsAreEmpty, externalExposureTrend, totalVulnerabilities, severityCounts]);

  // Keep project targets visible; coerce missing severity fields to 0.
  const displayTopResources = useMemo(() => {
    if (!Array.isArray(topResources)) return topResources;
    return topResources.map((resource) => ({
      ...resource,
      high: Number(resource.high) || 0,
      medium: Number(resource.medium) || 0,
      low: Number(resource.low) || 0,
      informational: Number(resource.informational) || 0,
      critical:
        resource.critical == null ? resource.critical : Number(resource.critical) || 0,
    }));
  }, [topResources]);

  const handleRowClick = async (risk) => {
    if (!risk.id) return;
    setSelectedRiskId(risk.id);
    setDrawerOpen(true);
    setDrawerTab(0);
    setRiskDetailsLoading(true);
    setIsAccepted(risk.accepted || risk.isAccepted || risk.is_accepted || false);
    setCurrentStatus(risk.status || "OPEN");
    setAcceptAccordionOpen(false);
    setAcceptReason("");

    const controller = new AbortController();
    const details = await getRiskDetails(risk.id, controller.signal);
    if (details) {
      setIsAccepted(details.accepted || details.isAccepted || details.is_accepted || false);
      setCurrentStatus(details.status || "OPEN");
    }
    setRiskDetailsLoading(false);
  };

  useEffect(() => {
    if (riskDetails) {
      setIsAccepted(riskDetails.accepted || riskDetails.isAccepted || riskDetails.is_accepted || false);
      setCurrentStatus(riskDetails.status || "OPEN");
    }
  }, [riskDetails]);

  const handleStatusChange = async (event) => {
    const newStatus = event.target.value;
    if (!selectedRiskId || newStatus === currentStatus || updatingStatus) return;

    setUpdatingStatus(true);
    try {
      const result = await updateRiskStatus(selectedRiskId, newStatus);
      if (result) {
        setCurrentStatus(newStatus);
        await getRiskDetails(selectedRiskId, null);
        getRisks(curProjectId, "all", filters, ITEMS_PER_PAGE, 0, null);
      }
    } catch (error) {
      console.error("Error updating risk status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRiskId(null);
    setDrawerTab(0);
    setIsAccepted(false);
    setCurrentStatus("OPEN");
    setAcceptAccordionOpen(false);
    setAcceptReason("");
  };

  const handleAcceptClick = () => {
    setAcceptAccordionOpen(!acceptAccordionOpen);
  };

  const handleConfirmAccept = async () => {
    if (!selectedRiskId) return;

    setAcceptingRisk(true);
    try {
      const result = await acceptRisk(selectedRiskId);
      if (result) {
        setIsAccepted(true);
        setAcceptAccordionOpen(false);
        setAcceptReason("");
        await getRiskDetails(selectedRiskId, null);
        getRisks(curProjectId, "all", filters, ITEMS_PER_PAGE, 0, null);
      }
    } catch (error) {
      console.error("Error accepting risk:", error);
    } finally {
      setAcceptingRisk(false);
    }
  };

  const handleUnaccept = async () => {
    if (!selectedRiskId) return;

    setUnacceptingRisk(true);
    try {
      const result = await unacceptRisk(selectedRiskId);
      if (result) {
        setIsAccepted(false);
        await getRiskDetails(selectedRiskId, null);
        getRisks(curProjectId, "all", filters, ITEMS_PER_PAGE, 0, null);
      }
    } catch (error) {
      console.error("Error unaccepting risk:", error);
    } finally {
      setUnacceptingRisk(false);
    }
  };

  return (
    <>
      <Helmet title={pageTitle} />
      <Box sx={{ p: 6 }}>
        <Stack spacing={6} sx={{ minHeight: "100vh", pb: 4 }}>
          <Stack spacing={4}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <V2PageHeader title={pageTitle} />
              {/* <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                sx={{
                  textTransform: "none",
                  fontSize: 14,
                  color: colors.neutral[700],
                  borderColor: colors.neutral[300],
                  borderRadius: "8px",
                  height: 36,
                }}
              >
                Save as...
              </Button>
              <Button
                variant="outlined"
                sx={{
                  textTransform: "none",
                  fontSize: 14,
                  color: colors.primary[300],
                  borderColor: colors.primary[300],
                  borderRadius: "8px",
                  height: 36,
                }}
              >
                Manage Rules
              </Button>
            </Stack> */}
            </Stack>

            {mode === MODE_THREATS && <ThreatInsightsPanel />}

            {mode === MODE_POSTURE && (
            <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 2,
                backgroundColor: "white",
                borderRadius: "8px",
                border: `1px solid ${colors.neutral[200]}`,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
                <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1.5, flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FilterIcon style={{ width: 18, height: 18, color: brandBlue }} />
                    <Typography sx={ { fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
                      Filters
                    </Typography>
                  </Stack>

                  <SafeTextField
                    size="small"
                    placeholder="Search finding title, URI, or keyword"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearchApply();
                      }
                    }}
                    sx={{
                      minWidth: 260,
                      "& .MuiInputBase-root": {
                        height: 36,
                        fontSize: 14,
                        lineHeight: "21px",
                      },
                      "& .MuiInputBase-input": {
                        fontSize: 14,
                        lineHeight: "21px",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: 14,
                        lineHeight: "21px",
                      },
                    }}
                  />

                  {/* CWE ID Filter */}
                  <Box
                    data-filter-button
                    sx={findingsFilterPillSx(Boolean(selectedVulnerability))}
                    onClick={(e) => {
                      e.stopPropagation();
                      const isOpen = Boolean(vulnerabilityAnchor);
                      setSeverityAnchor(null);
                      setStatusAnchor(null);
                      setResourcesAnchor(null);
                      setVulnerabilityAnchor(isOpen ? null : e.currentTarget);
                    }}
                  >
                    <BugReport sx={{ width: 16, height: 16, color: brandBlue, flexShrink: 0 }} />
                    <Typography className="filter-label" sx={findingsFilterPillLabelSx(Boolean(selectedVulnerability))}>
                      CWE ID
                    </Typography>
                    {selectedVulnerability ? (
                      <MuiCloseIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVulnerabilityFilterChange(selectedVulnerability);
                        }}
                        sx={{ fontSize: 14, color: brandBlue, flexShrink: 0, "&:hover": { color: colors.red[100] } }}
                      />
                    ) : (
                      <KeyboardArrowDown sx={{ fontSize: 16, color: brandBlue, flexShrink: 0 }} />
                    )}
                  </Box>

                  <Box
                    data-filter-button
                    sx={findingsFilterPillSx(Boolean(selectedSeverity))}
                    onClick={(e) => {
                      e.stopPropagation();
                      const isOpen = Boolean(severityAnchor);
                      setVulnerabilityAnchor(null);
                      setStatusAnchor(null);
                      setResourcesAnchor(null);
                      setSeverityAnchor(isOpen ? null : e.currentTarget);
                    }}
                  >
                    <Warning sx={{ width: 16, height: 16, color: brandBlue, flexShrink: 0 }} />
                    <Typography className="filter-label" sx={findingsFilterPillLabelSx(Boolean(selectedSeverity))}>
                      Severity
                    </Typography>
                    {selectedSeverity ? (
                      <MuiCloseIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSeverityFilterChange(selectedSeverity);
                        }}
                        sx={{ fontSize: 14, color: brandBlue, flexShrink: 0, "&:hover": { color: colors.red[100] } }}
                      />
                    ) : (
                      <KeyboardArrowDown sx={{ fontSize: 16, color: brandBlue, flexShrink: 0 }} />
                    )}
                  </Box>

                  <Box
                    data-filter-button
                    sx={findingsFilterPillSx(Boolean(selectedStatus))}
                    onClick={(e) => {
                      e.stopPropagation();
                      const isOpen = Boolean(statusAnchor);
                      setVulnerabilityAnchor(null);
                      setSeverityAnchor(null);
                      setResourcesAnchor(null);
                      setStatusAnchor(isOpen ? null : e.currentTarget);
                    }}
                  >
                    <Storage sx={{ width: 16, height: 16, color: brandBlue, flexShrink: 0 }} />
                    <Typography className="filter-label" sx={findingsFilterPillLabelSx(Boolean(selectedStatus))}>
                      Status
                    </Typography>
                    {selectedStatus ? (
                      <MuiCloseIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusFilterChange(selectedStatus);
                        }}
                        sx={{ fontSize: 14, color: brandBlue, flexShrink: 0, "&:hover": { color: colors.red[100] } }}
                      />
                    ) : (
                      <KeyboardArrowDown sx={{ fontSize: 16, color: brandBlue, flexShrink: 0 }} />
                    )}
                  </Box>

                  <Box
                    data-filter-button
                    sx={findingsFilterPillSx(Boolean(selectedResource))}
                    onClick={(e) => {
                      e.stopPropagation();
                      const isOpen = Boolean(resourcesAnchor);
                      setVulnerabilityAnchor(null);
                      setSeverityAnchor(null);
                      setStatusAnchor(null);
                      setResourcesAnchor(isOpen ? null : e.currentTarget);
                    }}
                  >
                    <Language sx={{ width: 16, height: 16, color: brandBlue, flexShrink: 0 }} />
                    <Typography className="filter-label" sx={findingsFilterPillLabelSx(Boolean(selectedResource))}>
                      Resource
                    </Typography>
                    {selectedResource ? (
                      <MuiCloseIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResourceFilterChange(selectedResource);
                        }}
                        sx={{ fontSize: 14, color: brandBlue, flexShrink: 0, "&:hover": { color: colors.red[100] } }}
                      />
                    ) : (
                      <KeyboardArrowDown sx={{ fontSize: 16, color: brandBlue, flexShrink: 0 }} />
                    )}
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
                  <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                    {totalVulnerabilities.toLocaleString()} vulnerabilities
                  </Typography>
                  <RefreshIconButton onClick={refreshAllData} disabled={isRefreshing} loading={isRefreshing} />
                </Stack>
              </Stack>

              {/* Filter Menus */}
              <Menu
                anchorEl={vulnerabilityAnchor}
                open={Boolean(vulnerabilityAnchor)}
                onClose={() => {
                  setVulnerabilityAnchor(null);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                MenuListProps={{
                  autoFocus: false,
                  onClick: (e) => e.stopPropagation(),
                  sx: {
                    py: 0,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1,
                    },
                  },
                }}
                PaperProps={{
                  sx: {
                    ...findingsFilterMenuPaperSx,
                    minWidth: 200,
                  },
                  onClick: (e) => e.stopPropagation(),
                }}
                disableAutoFocusItem
              >
                {uniqueVulnerabilities.length > 0 ? (
                  uniqueVulnerabilities.map((cweId) => (
                    <MenuItem
                      key={cweId}
                      onClick={() => {
                        handleVulnerabilityFilterChange(cweId);
                      }}
                    >
                      <FormControlLabel
                        control={<Checkbox checked={selectedVulnerability === cweId} size="small" />}
                        label={<Typography sx={ { fontSize: 14 }}>{cweId}</Typography>}
                        sx={{ pointerEvents: "none" }}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>No vulnerabilities found</Typography>
                  </MenuItem>
                )}
              </Menu>

              <Menu
                anchorEl={severityAnchor}
                open={Boolean(severityAnchor)}
                onClose={() => {
                  setSeverityAnchor(null);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                MenuListProps={{
                  autoFocus: false,
                  onClick: (e) => e.stopPropagation(),
                  sx: {
                    py: 0,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1,
                    },
                  },
                }}
                PaperProps={{
                  sx: {
                    ...findingsFilterMenuPaperSx,
                    minWidth: 200,
                  },
                  onClick: (e) => e.stopPropagation(),
                }}
                disableAutoFocusItem
              >
                {/* Severity filter options match what the backend actually
                    emits. Critical was here as a hard-coded option even
                    though the scanner (ZAP-based) never produces Critical —
                    the filter would return zero rows and confuse operators.
                    Informational added since backend now returns it. */}
                {["HIGH", "MEDIUM", "LOW", "INFORMATIONAL"].map((severity) => (
                  <MenuItem
                    key={severity}
                    onClick={() => {
                      handleSeverityFilterChange(severity);
                    }}
                    selected={selectedSeverity === severity}
                  >
                    <Typography sx={ { fontSize: 14, textTransform: "capitalize" }}>
                      {severity.toLowerCase()}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>

              <Menu
                anchorEl={statusAnchor}
                open={Boolean(statusAnchor)}
                onClose={() => {
                  setStatusAnchor(null);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                MenuListProps={{
                  autoFocus: false,
                  onClick: (e) => e.stopPropagation(),
                  sx: {
                    py: 0,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1,
                    },
                  },
                }}
                PaperProps={{
                  sx: {
                    ...findingsFilterMenuPaperSx,
                    minWidth: 200,
                  },
                  onClick: (e) => e.stopPropagation(),
                }}
                disableAutoFocusItem
              >
                {uniqueStatuses.length > 0 ? (
                  uniqueStatuses.map((status) => (
                    <MenuItem
                      key={status}
                      onClick={() => {
                        handleStatusFilterChange(status);
                      }}
                    >
                      <FormControlLabel
                        control={<Checkbox checked={selectedStatus === status} size="small" />}
                        label={<Typography sx={ { fontSize: 14 }}>{status}</Typography>}
                        sx={{ pointerEvents: "none" }}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>No statuses found</Typography>
                  </MenuItem>
                )}
              </Menu>

              {/* Resources Menu */}
              <Menu
                anchorEl={resourcesAnchor}
                open={Boolean(resourcesAnchor)}
                onClose={() => {
                  setResourcesAnchor(null);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                MenuListProps={{
                  autoFocus: false,
                  onClick: (e) => e.stopPropagation(),
                  sx: {
                    py: 0,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1,
                    },
                  },
                }}
                PaperProps={{
                  sx: {
                    ...findingsFilterMenuPaperSx,
                    minWidth: 200,
                    maxHeight: 300,
                    overflow: "auto",
                  },
                  onClick: (e) => e.stopPropagation(),
                }}
                disableAutoFocusItem
              >
                {uniqueResources.length > 0 ? (
                  uniqueResources.map((resource) => (
                    <MenuItem
                      key={resource.id}
                      onClick={() => {
                        handleResourceFilterChange(resource.id);
                      }}
                    >
                      <FormControlLabel
                        control={<Checkbox checked={selectedResource === resource.id} size="small" />}
                        label={<Typography sx={ { fontSize: 14 }}>{resource.label}</Typography>}
                        sx={{ pointerEvents: "none" }}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>No resources found</Typography>
                  </MenuItem>
                )}
              </Menu>

              {/* Active Filter Chips */}
              {(selectedSeverity || selectedVulnerability || selectedStatus || selectedResource || filters.search) && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {filters.search && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 0.5, borderRadius: "16px", bgcolor: colors.primary.alpha, border: `1px solid ${colors.primary[300]}` }}>
                      <Typography sx={ { fontSize: 12, color: colors.primary[300] }}>Search: {filters.search}</Typography>
                      <Box component="span" onClick={() => setFilters({ ...filters, search: null })} sx={{ cursor: "pointer", fontSize: 14, color: colors.primary[300], lineHeight: 1 }}>×</Box>
                    </Stack>
                  )}
                  {selectedVulnerability && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: "16px",
                        bgcolor: colors.primary.alpha,
                        border: `1px solid ${colors.primary[300]}`,
                      }}
                    >
                      <Typography sx={ { fontSize: 12, color: colors.primary[300] }}>
                        {selectedVulnerability}
                      </Typography>
                      <Box
                        component="span"
                        onClick={() => handleVulnerabilityFilterChange(selectedVulnerability)}
                        sx={{ cursor: "pointer", fontSize: 14, color: colors.primary[300], lineHeight: 1 }}
                      >
                        ×
                      </Box>
                    </Stack>
                  )}
                  {selectedSeverity && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: "16px",
                        bgcolor: colors.primary.alpha,
                        border: `1px solid ${colors.primary[300]}`,
                      }}
                    >
                      <Typography sx={ { fontSize: 12, color: colors.primary[300], textTransform: "capitalize" }}>
                        {selectedSeverity.toLowerCase()}
                      </Typography>
                      <Box
                        component="span"
                        onClick={() => handleSeverityFilterChange(selectedSeverity)}
                        sx={{ cursor: "pointer", fontSize: 14, color: colors.primary[300], lineHeight: 1 }}
                      >
                        ×
                      </Box>
                    </Stack>
                  )}
                  {selectedStatus && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: "16px",
                        bgcolor: colors.primary.alpha,
                        border: `1px solid ${colors.primary[300]}`,
                      }}
                    >
                      <Typography sx={ { fontSize: 12, color: colors.primary[300] }}>{selectedStatus}</Typography>
                      <Box
                        component="span"
                        onClick={() => handleStatusFilterChange(selectedStatus)}
                        sx={{ cursor: "pointer", fontSize: 14, color: colors.primary[300], lineHeight: 1 }}
                      >
                        ×
                      </Box>
                    </Stack>
                  )}
                  {selectedResource && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: "16px",
                        bgcolor: colors.primary.alpha,
                        border: `1px solid ${colors.primary[300]}`,
                      }}
                    >
                      <Typography sx={ { fontSize: 12, color: colors.primary[300] }}>{selectedResourceLabel}</Typography>
                      <Box
                        component="span"
                        onClick={() => handleResourceFilterChange(selectedResource)}
                        sx={{ cursor: "pointer", fontSize: 14, color: colors.primary[300], lineHeight: 1 }}
                      >
                        ×
                      </Box>
                    </Stack>
                  )}
                  <Button
                    onClick={clearAllFilters}
                    sx={{
                      fontSize: 12,
                      color: colors.primary[300],
                      textTransform: "none",
                      minWidth: "auto",
                      px: 1,
                    }}
                  >
                    Clear all
                  </Button>
                </Stack>
              )}
            </Box>

            <Stack direction="row" spacing={4}>
              <Box
                sx={{
                  flex: 1,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  border: `1px solid ${colors.neutral[200]}`,
                  p: 4,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                    Vulnerabilities by Severity
                  </Typography>
                  <Link
                    component="button"
                    onClick={() => {
                      setFilters({ ...filters, threat_level: null });
                    }}
                    sx={ { fontSize: 12, color: colors.primary[300], textDecoration: "none", cursor: "pointer" }}
                  >
                    View all
                  </Link>
                </Stack>
                {severityCounts === null ? (
                  <Stack spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} variant="rectangular" height={24} />
                    ))}
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    {[
                      // Critical is only rendered when the backend actually
                      // returns the field. Current scanner (ZAP-based) does
                      // not emit Critical — the row used to render as a
                      // permanent "0" that suggested absent coverage.
                      ...(severityCounts.critical != null
                        ? [{ label: "Critical", value: severityCounts.critical, color: colors.red[200], dot: colors.red[200], filterValue: "CRITICAL" }]
                        : []),
                      { label: "High", value: severityCounts.high || 0, color: "#FF6B35", dot: "#FF6B35", filterValue: "HIGH" },
                      { label: "Medium", value: severityCounts.medium || 0, color: "#A68705", dot: colors.yellow[200], filterValue: "MEDIUM" },
                      { label: "Low", value: severityCounts.low || 0, color: colors.neutral[700], dot: colors.green[300], filterValue: "LOW" },
                      { label: "Informational", value: severityCounts.informational || 0, color: colors.neutral[600], dot: colors.neutral[400], filterValue: "INFORMATIONAL" },
                    ].map((item) => (
                      <Stack
                        key={item.label}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => setFilters({ ...filters, threat_level: item.filterValue })}
                        sx={{
                          cursor: "pointer",
                          py: 1,
                          px: 1,
                          borderRadius: "4px",
                          bgcolor: filters.threat_level === item.filterValue ? colors.primary[10] : "transparent",
                          "&:hover": { bgcolor: colors.neutral[50] },
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.dot, flexShrink: 0 }} />
                        <Typography sx={ { fontSize: 14, color: colors.neutral[800], flex: 1, ml: 2, whiteSpace: "nowrap" }}>{item.label}</Typography>
                        <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], minWidth: 40, textAlign: "right" }}>
                          {item.value.toLocaleString()}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1.5,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  border: `1px solid ${colors.neutral[200]}`,
                  p: 4,
                }}
              >
                <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], mb: 3 }}>
                  External Exposure Trend (7 Days)
                </Typography>
                {externalExposureTrend === null && !findingsAreEmpty ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 170 }}>
                    <CircularProgress size={24} sx={{ color: colors.neutral[400] }} />
                  </Stack>
                ) : !Array.isArray(exposureTrendChartData) || exposureTrendChartData.length === 0 ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 170 }}>
                    <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>No trend data available</Typography>
                  </Stack>
                ) : (
                  <Box sx={{ width: "100%", pr: 2 }}>
                    <Chart
                      key={findingsAreEmpty ? "exposure-trend-empty" : "exposure-trend-live"}
                      options={{
                        chart: {
                          type: "area",
                          toolbar: { show: false },
                          zoom: { enabled: false },
                          animations: { enabled: true },
                          sparkline: { enabled: false },
                          foreColor: "#696969",
                        },
                        dataLabels: { enabled: false },
                        colors: [colors.primary[300]],
                        stroke: { curve: "smooth", width: 2 },
                        fill: apexVerticalAreaGradient({
                          toColor: "rgba(99, 113, 247, 0.25)",
                        }),
                        markers: { size: 0 },
                        xaxis: {
                          type: "datetime",
                          labels: {
                            datetimeUTC: false,
                            style: { fontSize: "11px", colors: "#696969" },
                          },
                          axisBorder: { show: false },
                          axisTicks: { show: false },
                        },
                        yaxis: {
                          min: 0,
                          forceNiceScale: true,
                          tickAmount: 3,
                          title: apexNoOfYAxisTitle("No. of Exposures"),
                          labels: {
                            style: { colors: "#696969", fontSize: "11px" },
                            formatter: (val) => Math.round(val),
                          },
                        },
                        grid: {
                          show: true,
                          borderColor: "#e6e6e6",
                          strokeDashArray: 0,
                          padding: { left: 8 },
                          xaxis: { lines: { show: false } },
                          yaxis: { lines: { show: true } },
                        },
                        tooltip: {
                          enabled: true,
                          x: { format: "MMM dd, yyyy" },
                        },
                        legend: { show: false },
                      }}
                      series={[
                        {
                          name: "Total Exposed",
                          data: exposureTrendChartData.map((item) => [
                            new Date(item.date).getTime(),
                            item.total || 0,
                          ]),
                        },
                      ]}
                      type="area"
                      height={170}
                    />
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1.5,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  border: `1px solid ${colors.neutral[200]}`,
                  p: 4,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                    Top Vulnerable Resources 
                  </Typography>
                  <Link
                    component="button"
                    onClick={() => {
                      setFilters({ ...filters, target_id: null });
                    }}
                    sx={ { fontSize: 12, color: colors.primary[300], textDecoration: "none", cursor: "pointer" }}
                  >
                    View all
                  </Link>
                </Stack>
                {!Array.isArray(displayTopResources) ? (
                  <Stack spacing={2}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} variant="rectangular" height={24} />
                    ))}
                  </Stack>
                ) : displayTopResources.length === 0 ? (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 1, px: 1, opacity: 0.7 }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                      <Language sx={{ fontSize: 20, color: colors.neutral[400] }} />
                      <Typography sx={{ fontSize: 14, color: colors.neutral[500] }}>—</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CountBadge count={0} type="H" />
                      <CountBadge count={0} type="M" />
                      <CountBadge count={0} type="L" />
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    {displayTopResources.slice(0, 5).map((resource, index) => (
                      <Stack
                        key={resource.targetId || resource.target_id || index}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => handleResourceFilterChange(resource.targetId || resource.target_id || null)}
                        sx={{
                          cursor: "pointer",
                          py: 1,
                          px: 1,
                          borderRadius: "4px",
                          bgcolor: filters.target_id === (resource.targetId || resource.target_id) ? colors.primary[10] : "transparent",
                          "&:hover": { bgcolor: colors.neutral[50] },
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                          <Language sx={{ fontSize: 20, color: colors.primary[300] }} />
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: colors.neutral[800],
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {resource.targetLabel || resource.target || "—"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {resource.critical != null && (
                            <CountBadge count={resource.critical || 0} type="C" />
                          )}
                          <CountBadge count={resource.high || 0} type="H" />
                          <CountBadge count={resource.medium || 0} type="M" />
                          <CountBadge count={resource.low || 0} type="L" />
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>

            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "8px",
                border: `1px solid ${colors.neutral[200]}`,
                overflow: "hidden",
              }}
            >
              <Box ref={scrollContainerRef}>
                <ReactPerfectScrollbar>
                  <Table sx={{ minWidth: 1400 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: colors.neutral[50] }}>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 200 }}>Finding</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 150 }}>Resource</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 180 }}>Component</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 100 }}>Status</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 100 }}>Severity</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 110 }}>Risk Score</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 150 }}>Timeline</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 80 }}>CWE ID</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 100 }}>Tags</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 2, minWidth: 120 }}>Project Name</TableCell>
                        <TableCell sx={{ ...headTypographyStyle, p: 1, width: 40 }} aria-label="actions" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 10 }}>
                            <Stack alignItems="center" spacing={2}>
                              <CircularProgress size={28} />
                              <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                                Loading vulnerabilities...
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : !Array.isArray(allRisks) || allRisks.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                            <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                              No vulnerabilities found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {allRisks.map((risk, index) => (
                            <TableRow
                              key={risk.id || index}
                              sx={{
                                "&:hover": { bgcolor: colors.neutral[50], cursor: "pointer" },
                                cursor: "pointer",
                              }}
                              onClick={() => handleRowClick(risk)}
                            >
                              <TableCell sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Link
                                    href={risk.reference || "#"}
                                    target="_blank"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      fontSize: 14,
                                      color: colors.neutral[1000],
                                      textDecoration: "none",
                                      "&:hover": { textDecoration: "none" },
                                    }}
                                  >
                                    {risk.title || "N/A"}
                                  </Link>
                                  <ExternalLinkIcon style={{ width: 12, height: 12, color: colors.neutral[400] }} />
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ p: 2 }}>
                                <Stack>
                                  <Typography sx={{ ...cellTypographyStyle }}>{risk.target}</Typography>
                                  <Typography sx={ { fontSize: 12, color: colors.neutral[500] }}>
                                    Web Application
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ p: 2 }}>
                                <Stack>
                                  <Typography sx={{ ...cellTypographyStyle }}>
                                    {(() => {
                                      const riskDef = risk.risk_definition || risk.riskDefinition || {};
                                      const additionalInfo = riskDef.additional_info || riskDef.additionalInfo || {};
                                      const instances = additionalInfo.Instances || [];
                                      
                                      if (instances.length === 0) {
                                        // Fallback to URL if no instances
                                        if (risk.url) {
                                          try {
                                            return new URL(risk.url).pathname;
                                          } catch {
                                            return risk.url;
                                          }
                                        }
                                        return "—";
                                      } else if (instances.length === 1) {
                                        // Show URI for single instance
                                        return instances[0].uri || "—";
                                      } else {
                                        // Show "multiple" for multiple instances
                                        return "multiple";
                                      }
                                    })()}
                                  </Typography>
                                  <Typography sx={ { fontSize: 12, color: colors.neutral[500] }}>Endpoint</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ p: 2 }}>{getStatusLabel(risk.status)}</TableCell>
                              <TableCell sx={{ p: 2 }}>{getSeverityLabel(risk.threat_level)}</TableCell>
                              <TableCell sx={{ p: 2 }}>
                                {/* Risk Score = scanner's threat-informed 0-100 model, not just raw severity. */}
                                {typeof risk.risk_score === "number" ? (() => {
                                  const band = (risk.risk_band || "unknown").toLowerCase();
                                  const bandColors = {
                                    critical: { bg: colors.red[10], border: colors.red[100], text: colors.red[300] },
                                    high:     { bg: colors.red[10], border: colors.red[100], text: colors.red[300] },
                                    moderate: { bg: colors.yellow?.[10] || colors.neutral[50], border: colors.yellow?.[100] || colors.neutral[300], text: colors.yellow?.[300] || colors.neutral[800] },
                                    low:      { bg: colors.green[10], border: colors.green[100], text: colors.green[300] },
                                    unknown:  { bg: colors.neutral[50], border: colors.neutral[200], text: colors.neutral[600] },
                                  }[band] || { bg: colors.neutral[50], border: colors.neutral[200], text: colors.neutral[600] };
                                  return (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: "6px", bgcolor: bandColors.bg, border: `1px solid ${bandColors.border}` }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: bandColors.text, fontFamily: "monospace" }}>
                                          {risk.risk_score}
                                        </Typography>
                                      </Box>
                                      <Typography sx={{ fontSize: 11, color: colors.neutral[600], textTransform: "capitalize" }}>{band}</Typography>
                                    </Stack>
                                  );
                                })() : (
                                  <Typography sx={{ ...cellTypographyStyle, color: colors.neutral[400] }}>—</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ p: 2 }}>
                                {/* Lifecycle badge + firstSeen/lastSeen (short relative + absolute title). */}
                                {(() => {
                                  const lifecycle = (risk.lifecycle || "").toLowerCase();
                                  const firstSeen = risk.first_seen_at;
                                  const lastSeen = risk.last_seen_at;
                                  const relative = (iso) => {
                                    if (!iso) return "—";
                                    const d = new Date(iso);
                                    const ms = Date.now() - d.getTime();
                                    const days = Math.floor(ms / 86400000);
                                    if (days < 1) return "today";
                                    if (days === 1) return "1d ago";
                                    if (days < 30) return `${days}d ago`;
                                    const months = Math.floor(days / 30);
                                    if (months < 12) return `${months}mo ago`;
                                    return `${Math.floor(months / 12)}y ago`;
                                  };
                                  const lifecycleColors = {
                                    new:       { bg: colors.red[10] || "#FEF2F2", border: colors.red[100] || "#FECACA", text: colors.red[300] || "#B91C1C" },
                                    returning: { bg: "#FEF3C7", border: "#FDE68A", text: "#B45309" },
                                    closed:    { bg: colors.green[10], border: colors.green[100], text: colors.green[300] },
                                    open:      { bg: colors.neutral[50], border: colors.neutral[200], text: colors.neutral[700] },
                                  }[lifecycle];
                                  return (
                                    <Stack spacing={0.5}>
                                      {lifecycleColors && (
                                        <Box sx={{ display: "inline-block", width: "fit-content", px: 1.5, py: 0.25, borderRadius: "4px", bgcolor: lifecycleColors.bg, border: `1px solid ${lifecycleColors.border}` }}>
                                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: lifecycleColors.text, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                            {lifecycle}
                                          </Typography>
                                        </Box>
                                      )}
                                      <Typography sx={{ fontSize: 11, color: colors.neutral[600] }} title={firstSeen ? `First: ${new Date(firstSeen).toLocaleString()}` : ""}>
                                        First: {relative(firstSeen)}
                                      </Typography>
                                      <Typography sx={{ fontSize: 11, color: colors.neutral[500] }} title={lastSeen ? `Last: ${new Date(lastSeen).toLocaleString()}` : ""}>
                                        Last: {relative(lastSeen)}
                                      </Typography>
                                    </Stack>
                                  );
                                })()}
                              </TableCell>
                              <TableCell sx={{ p: 2 }}>
                                <Typography sx={{ ...cellTypographyStyle }}>
                                  {(() => {
                                    const riskDef = risk.risk_definition || risk.riskDefinition || {};
                                    const additionalInfo = riskDef.additional_info || riskDef.additionalInfo || {};
                                    const cweId = additionalInfo["CWE Id"] || risk.cwe_id || "";
                                    if (!cweId || cweId === "—") return "—";
                                    const numericId = String(cweId).replace(/^CWE-?/i, "");
                                    return `CWE-${numericId}`;
                                  })()}
                                </Typography>
                              </TableCell>
                              {/* <TableCell sx={{ p: 2 }}>
                                <Typography sx={{ ...cellTypographyStyle }}>—</Typography>
                              </TableCell> */}
                              <TableCell sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                  {(() => {
                                    const riskDef = risk.risk_definition || risk.riskDefinition || {};
                                    const additionalInfo = riskDef.additional_info || riskDef.additionalInfo || {};
                                    const tags = additionalInfo.tags || risk.tags || [];
                                    if (Array.isArray(tags) && tags.length > 0) {
                                      return tags.slice(0, 3).map((tag, idx) => {
                                        const tagValue = typeof tag === "string" ? tag : tag.tag || tag;
                                        return (
                                          <Box
                                            key={idx}
                                            sx={{
                                              px: 1.5,
                                              py: 0.5,
                                              borderRadius: "4px",
                                              bgcolor: colors.primary[10],
                                              border: `1px solid ${colors.primary[100]}`,
                                            }}
                                          >
                                            <Typography sx={ { fontSize: 12, color: colors.primary[300] }}>
                                              {tagValue}
                                            </Typography>
                                          </Box>
                                        );
                                      });
                                    }
                                    return <Typography sx={{ ...cellTypographyStyle }}>—</Typography>;
                                  })()}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ p: 2 }}>
                                <Typography sx={{ ...cellTypographyStyle }}>
                                  {(() => {
                                    if (!projectList || !curProjectId) return "—";
                                    if (curProjectId === "all") return "All projects";
                                    if (curProjectId === "default") return "Default";
                                    const project = projectList.find((p) => p.id === curProjectId);
                                    return project?.name || "—";
                                  })()}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ p: 1, width: 40 }} onClick={(e) => e.stopPropagation()}>
                                {/* Row-level actions: same menu as the drawer header, so operators */}
                                {/* can take quick actions (add note, assign owner, suppress class, */}
                                {/* download report) without opening the drawer at all. */}
                                {(risk.finding_id || risk.findingId) && (
                                  <DastActions
                                    variant="row"
                                    finding={risk}
                                    colors={colors}
                                    onSaved={() => getRisks(curProjectId, "all", filters, ITEMS_PER_PAGE, 0, null)}
                                    updateFinding={updateFinding}
                                    stopScan={stopScan}
                                    getScanReportUrl={getScanReportUrl}
                                    createSuppressionRule={createSuppressionRule}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}
                    </TableBody>
                  </Table>
                  {/* Sentinel element for scroll pagination */}
                  {hasMore && allRisks.length >= ITEMS_PER_PAGE && <Box ref={sentinelRef} sx={{ height: 1, width: "100%" }} />}
                  {isLoadingMore && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  {/* {!hasMore && allRisks.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>
                    No more vulnerabilities to load
                  </Typography>
                </Box>
              )} */}
                </ReactPerfectScrollbar>
              </Box>
            </Box>
            </>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Risk Details Drawer — only rendered in posture mode; Threat
          Insights ships its own drawer via ThreatInsightsPanel. */}
      {mode === MODE_POSTURE && (
      <Modal open={drawerOpen} onClose={handleCloseDrawer}>
        <Slide direction="left" in={drawerOpen} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "50%",
              minWidth: 600,
              height: "100vh",
              maxHeight: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
              outline: "none",
              border: "none",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100vh",
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  bgcolor: colors.neutral[50],
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: colors.neutral[300],
                  borderRadius: "4px",
                  "&:hover": {
                    bgcolor: colors.neutral[400],
                  },
                },
              }}
            >
              <Box sx={{ minHeight: "100%" }}>
                {/* Header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 4, borderBottom: `1px solid ${colors.neutral[200]}` }}
                >
                  <Stack spacing={1} sx={{ flex: 1 }}>
                    <Typography sx={ { fontSize: 20, fontWeight: "medium", color: colors.neutral[1000] }}>
                      {riskDetails?.riskDefinition?.title || riskDetails?.title || riskDetails?.risk_definition?.title || "Risk Details"}
                    </Typography>
                    {riskDetails?.id && (
                      <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.neutral[500] }}>
                        ID: {riskDetails.id}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {isAccepted ? (
                      <Button
                        variant="outlined"
                        onClick={handleUnaccept}
                        disabled={unacceptingRisk}
                        sx={{
                          textTransform: "none",
                          fontSize: 14,
                          color: colors.primary[300],
                          borderColor: colors.primary[300],
                          borderRadius: "8px",
                          px: 3,
                          py: 1,
                          "&:hover": {
                            borderColor: colors.primary[300],
                            bgcolor: colors.primary.alpha,
                          },
                          "&:disabled": {
                            borderColor: colors.neutral[300],
                            color: colors.neutral[500],
                          },
                        }}
                      >
                        {unacceptingRisk ? "Unaccepting..." : "Unaccept"}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={handleAcceptClick}
                        sx={{
                          textTransform: "none",
                          fontSize: 14,
                          color: colors.primary[300],
                          borderColor: colors.primary[300],
                          borderRadius: "8px",
                          px: 3,
                          py: 1,
                          "&:hover": {
                            borderColor: colors.primary[300],
                            bgcolor: colors.primary.alpha,
                          },
                        }}
                      >
                        Accept
                      </Button>
                    )}
                    {/* Per-finding action surface: menu-driven, each item opens its own */}
                    {/* focused modal (notes / owner / state / exception / suppression / */}
                    {/* report / cancel). Only renders when the enriched risk payload */}
                    {/* carries a finding_id — old findings fall back to the Accept button. */}
                    {(riskDetails?.finding_id || riskDetails?.findingId) && (
                      <DastActions
                        finding={riskDetails}
                        colors={colors}
                        onSaved={() => getRiskDetails(selectedRiskId, null)}
                        updateFinding={updateFinding}
                        stopScan={stopScan}
                        getScanReportUrl={getScanReportUrl}
                        createSuppressionRule={createSuppressionRule}
                      />
                    )}
                    <IconButton onClick={handleCloseDrawer}>
                      <CloseIcon style={{ width: 24, height: 24, color: colors.neutral[600] }} />
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Metadata Section */}
                {riskDetails &&
                  (() => {
                    const riskDef = riskDetails.riskDefinition || riskDetails.risk_definition || {};
                    const additionalInfo = riskDef.additional_info || {};
                    const cweId = additionalInfo["CWE Id"] || riskDetails.cweId || riskDetails.cwe_id;
                    const wascId = additionalInfo["WASC Id"];
                    const threatLevel = riskDef.threat_level || riskDetails.threatLevel || riskDetails.threat_level || "MEDIUM";
                    const scanType = riskDef.scan_type || riskDetails.scanType || riskDetails.scan_type;
                    const tags = additionalInfo.tags || [];
                    const createdAt = riskDetails.createdAt || riskDetails.created_at;
                    const lastDetectedAt = riskDetails.lastDetectedAt || riskDetails.last_detected_at;

                    const formatDate = (dateStr) => {
                      if (!dateStr) return null;
                      const date = new Date(dateStr);
                      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                    };

                    return (
                      <Box sx={{ borderBottom: `1px solid ${colors.neutral[200]}` }}>
                        {/* Status Bar */}
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ px: 4, py: 2, bgcolor: colors.neutral[50] }}>
                          {getSeverityLabel(threatLevel)}
                          
                          {currentStatus === "CLOSED" ? (
                            <Box sx={{ px: 2, py: 0.5, borderRadius: "20px", bgcolor: colors.green[10], border: `1px solid ${colors.green[100]}` }}>
                              <Typography sx={ { fontSize: 12, color: colors.green[300] }}>Closed</Typography>
                            </Box>
                          ) : (
                            <Box sx={{ px: 2, py: 0.5, borderRadius: "20px", bgcolor: colors.neutral[10], border: `1px solid ${colors.neutral[400]}` }}>
                              <Typography sx={ { fontSize: 12, color: colors.neutral[700] }}>Open</Typography>
                            </Box>
                          )}

                          {isAccepted && (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 2, py: 0.5, borderRadius: "20px", bgcolor: colors.green[10], border: `1px solid ${colors.green[100]}` }}>
                              <CheckCircle sx={{ width: 14, height: 14, color: colors.green[300] }} />
                              <Typography sx={ { fontSize: 12, color: colors.green[300] }}>Accepted</Typography>
                            </Stack>
                          )}

                          {scanType && (
                            <Box sx={{ px: 2, py: 0.5, borderRadius: "20px", bgcolor: colors.primary[10], border: `1px solid ${colors.primary[100]}` }}>
                              <Typography sx={ { fontSize: 12, color: colors.primary[300] }}>{scanType}</Typography>
                            </Box>
                          )}
                        </Stack>

                        {/* Info Grid */}
                        <Box sx={{ px: 4, py: 3, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 3 }}>
                          {cweId && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>CWE ID</Typography>
                              <Link
                                href={`https://cwe.mitre.org/data/definitions/${String(cweId).replace("CWE-", "")}.html`}
                                target="_blank"
                                sx={ { fontSize: 14, color: colors.primary[300], textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                              >
                                CWE-{String(cweId).replace("CWE-", "")} ↗
                              </Link>
                            </Stack>
                          )}

                          {wascId && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>WASC ID</Typography>
                              <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>{wascId}</Typography>
                            </Stack>
                          )}

                          {createdAt && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>First Detected</Typography>
                              <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>{formatDate(createdAt) || "—"}</Typography>
                            </Stack>
                          )}

                          {lastDetectedAt && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>Last Detected</Typography>
                              <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>{formatDate(lastDetectedAt) || "—"}</Typography>
                            </Stack>
                          )}

                          {/* Scanner-enriched identity fields (surfaced for operators who need to */}
                          {/* chase a finding across scans or jump to the raw scanner API). */}
                          {(riskDetails.first_seen_at || riskDetails.firstSeenAt) && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>First Seen (scanner)</Typography>
                              <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>{formatDate(riskDetails.first_seen_at || riskDetails.firstSeenAt)}</Typography>
                            </Stack>
                          )}
                          {(riskDetails.last_seen_at || riskDetails.lastSeenAt) && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>Last Seen (scanner)</Typography>
                              <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>{formatDate(riskDetails.last_seen_at || riskDetails.lastSeenAt)}</Typography>
                            </Stack>
                          )}
                          {riskDetails.lifecycle && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>Lifecycle</Typography>
                              <Typography sx={ { fontSize: 14, color: colors.neutral[800], textTransform: "capitalize" }}>{riskDetails.lifecycle}</Typography>
                            </Stack>
                          )}
                          {riskDetails.owner && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>Owner</Typography>
                              <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>{riskDetails.owner}</Typography>
                            </Stack>
                          )}
                        </Box>

                        {/* Risk Score — threat-informed 0-100 score with human-readable rationale. */}
                        {typeof riskDetails.risk_score === "number" && (
                          <Box sx={{ px: 4, pb: 3 }}>
                            <Accordion elevation={0} sx={{ border: `1px solid ${colors.neutral[200]}`, "&:before": { display: "none" } }}>
                              <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: colors.neutral[50] }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Box sx={{ px: 2, py: 0.5, borderRadius: "6px", bgcolor: colors.primary[10], border: `1px solid ${colors.primary[100]}` }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.primary[300], fontFamily: "monospace" }}>
                                      {riskDetails.risk_score} / 100
                                    </Typography>
                                  </Box>
                                  <Typography sx={{ fontSize: 13, color: colors.neutral[800], textTransform: "capitalize" }}>
                                    {riskDetails.risk_band || "unknown"} · {riskDetails.risk_priority || "—"}
                                  </Typography>
                                  <Typography sx={{ fontSize: 11, color: colors.neutral[500] }}>Why this score?</Typography>
                                </Stack>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Stack spacing={2}>
                                  {Array.isArray(riskDetails.risk_explanation?.rationale) && riskDetails.risk_explanation.rationale.length > 0 && (
                                    <Stack component="ul" sx={{ pl: 3, m: 0 }} spacing={0.5}>
                                      {riskDetails.risk_explanation.rationale.map((line, idx) => (
                                        <Typography key={idx} component="li" sx={{ fontSize: 13, color: colors.neutral[800] }}>{line}</Typography>
                                      ))}
                                    </Stack>
                                  )}
                                  {riskDetails.risk_explanation?.asset && (
                                    <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
                                      Asset: exposure={riskDetails.risk_explanation.asset.exposure}, criticality={riskDetails.risk_explanation.asset.criticality}, env={riskDetails.risk_explanation.asset.environment}
                                    </Typography>
                                  )}
                                  {riskDetails.risk_intelligence_stale && (
                                    <Typography sx={{ fontSize: 12, color: colors.yellow?.[400] || "#B45309" }}>
                                      ⚠ Intelligence feed is stale — score may be conservative
                                    </Typography>
                                  )}
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}

                        {/* Intelligence enrichment — CWE/CVE chips with freshness. */}
                        {(Array.isArray(riskDetails.intelligence_cwes) && riskDetails.intelligence_cwes.length > 0) ||
                         (Array.isArray(riskDetails.intelligence_cves) && riskDetails.intelligence_cves.length > 0) ? (
                          <Stack spacing={1.5} sx={{ px: 4, pb: 3 }}>
                            <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>Threat Intelligence</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                              {(riskDetails.intelligence_cwes || []).map((cwe, idx) => (
                                <Link
                                  key={`cwe-${idx}`}
                                  href={cwe.url || `https://cwe.mitre.org/data/definitions/${String(cwe.id || "").replace(/^CWE-?/i, "")}.html`}
                                  target="_blank"
                                  sx={{ textDecoration: "none", px: 1.5, py: 0.5, borderRadius: "6px", bgcolor: colors.primary[10], border: `1px solid ${colors.primary[100]}`, fontSize: 12, color: colors.primary[300], "&:hover": { bgcolor: colors.primary[20] } }}
                                >
                                  {cwe.id} · {cwe.name} ↗
                                </Link>
                              ))}
                              {(riskDetails.intelligence_cves || []).map((cve, idx) => (
                                <Link
                                  key={`cve-${idx}`}
                                  href={cve.url || `https://nvd.nist.gov/vuln/detail/${cve.id}`}
                                  target="_blank"
                                  sx={{ textDecoration: "none", px: 1.5, py: 0.5, borderRadius: "6px", bgcolor: colors.red?.[10] || "#FEF2F2", border: `1px solid ${colors.red?.[100] || "#FECACA"}`, fontSize: 12, color: colors.red?.[300] || "#B91C1C", "&:hover": { opacity: 0.9 } }}
                                >
                                  {cve.id}{cve.cvssScore ? ` · CVSS ${cve.cvssScore}` : ""} ↗
                                </Link>
                              ))}
                            </Stack>
                          </Stack>
                        ) : null}

                        {/* Occurrence History — the per-scan evidence trail (replaces the drawer's */}
                        {/* previous reliance on only instances[0]). */}
                        {Array.isArray(riskDetails.occurrence_history) && riskDetails.occurrence_history.length > 0 && (
                          <Stack spacing={1.5} sx={{ px: 4, pb: 3 }}>
                            <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              Occurrence History · {riskDetails.occurrence_history.length}
                            </Typography>
                            <Stack spacing={1}>
                              {riskDetails.occurrence_history.slice(0, 25).map((occ, idx) => (
                                <Box key={occ.occurrenceId || idx} sx={{ pl: 2, borderLeft: `2px solid ${colors.primary[100]}`, py: 0.5 }}>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography sx={{ fontSize: 11, color: colors.neutral[500], fontFamily: "monospace", minWidth: 160 }}>
                                      {occ.observedAt ? new Date(occ.observedAt).toLocaleString() : "—"}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.primary[300], fontFamily: "monospace", fontWeight: 600 }}>
                                      {occ.method || "GET"}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.neutral[800], fontFamily: "monospace", wordBreak: "break-all" }}>
                                      {occ.url || "—"}
                                    </Typography>
                                  </Stack>
                                  {occ.evidence && (
                                    <Typography sx={{ fontSize: 11, color: colors.neutral[600], fontFamily: "monospace", mt: 0.5, px: 1, py: 0.5, bgcolor: colors.neutral[50], borderRadius: "4px", wordBreak: "break-all" }}>
                                      {String(occ.evidence).slice(0, 220)}{String(occ.evidence).length > 220 ? "…" : ""}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </Stack>
                        )}

                        {/* Triage summary — compact READ view of what analysts have done to this */}
                        {/* finding. The write surface moved into the Actions ▾ menu in the drawer */}
                        {/* header + row 3-dot menu, so the drawer scroll stays short and focused. */}
                        {(riskDetails.finding_id || riskDetails.findingId) && (
                          <Box sx={{ px: 4, pb: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                              <Typography sx={{ fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Triage
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: colors.neutral[400] }}>
                                — use Actions ▾ above to change
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              sx={{ gap: 1 }}
                            >
                              {(() => {
                                const state = (riskDetails.analyst_state || riskDetails.analystState || "open").replace("_", " ");
                                const stateColor = {
                                  open: colors.neutral[700],
                                  "false positive": colors.green?.[300] || "#047857",
                                  accepted: colors.yellow?.[400] || "#B45309",
                                  fixed: colors.green?.[300] || "#047857",
                                }[state] || colors.neutral[700];
                                return (
                                  <Box sx={{ px: 1.5, py: 0.75, borderRadius: "6px", bgcolor: colors.neutral[50], border: `1px solid ${colors.neutral[200]}` }}>
                                    <Typography sx={{ fontSize: 11, color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.08em" }}>Analyst state</Typography>
                                    <Typography sx={{ fontSize: 13, color: stateColor, fontWeight: 600, textTransform: "capitalize" }}>{state}</Typography>
                                  </Box>
                                );
                              })()}
                              {(riskDetails.owner) && (
                                <Box sx={{ px: 1.5, py: 0.75, borderRadius: "6px", bgcolor: colors.neutral[50], border: `1px solid ${colors.neutral[200]}` }}>
                                  <Typography sx={{ fontSize: 11, color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.08em" }}>Owner</Typography>
                                  <Typography sx={{ fontSize: 13, color: colors.neutral[1000], fontWeight: 500 }}>{riskDetails.owner}</Typography>
                                </Box>
                              )}
                              {(() => {
                                const es = riskDetails.exception_status || riskDetails.exceptionStatus;
                                if (!es || es === "none") return null;
                                const expiry = riskDetails.exception_expires_at || riskDetails.exceptionExpiresAt;
                                const expiryStr = expiry ? ` until ${new Date(expiry).toLocaleDateString()}` : " · permanent";
                                return (
                                  <Box sx={{ px: 1.5, py: 0.75, borderRadius: "6px", bgcolor: "#FEF3C7", border: `1px solid #FDE68A` }}>
                                    <Typography sx={{ fontSize: 11, color: "#B45309", textTransform: "uppercase", letterSpacing: "0.08em" }}>Exception</Typography>
                                    <Typography sx={{ fontSize: 13, color: "#78350F", fontWeight: 600, textTransform: "capitalize" }}>{es}{expiryStr}</Typography>
                                  </Box>
                                );
                              })()}
                              {(() => {
                                const ss = riskDetails.suppression_status || riskDetails.suppressionStatus;
                                if (!ss || ss === "visible") return null;
                                return (
                                  <Box sx={{ px: 1.5, py: 0.75, borderRadius: "6px", bgcolor: "#F5F3FF", border: `1px solid #DDD6FE` }}>
                                    <Typography sx={{ fontSize: 11, color: "#6D28D9", textTransform: "uppercase", letterSpacing: "0.08em" }}>Suppression</Typography>
                                    <Typography sx={{ fontSize: 13, color: "#5B21B6", fontWeight: 600, textTransform: "capitalize" }}>{ss}</Typography>
                                  </Box>
                                );
                              })()}
                            </Stack>
                            {(riskDetails.analyst_notes || riskDetails.analystNotes) && (
                              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "6px", bgcolor: colors.neutral[50], border: `1px solid ${colors.neutral[200]}` }}>
                                <Typography sx={{ fontSize: 11, color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>Analyst note</Typography>
                                <Typography sx={{ fontSize: 13, color: colors.neutral[800], whiteSpace: "pre-wrap" }}>
                                  {riskDetails.analyst_notes || riskDetails.analystNotes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Tags */}
                        {tags.length > 0 && (
                          <Stack spacing={1.5} sx={{ px: 4, pb: 3 }}>
                            <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[500], textTransform: "uppercase", letterSpacing: "0.5px" }}>Tags</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                              {tags.map((tag, idx) => {
                                const tagName = typeof tag === "string" ? tag : tag.tag;
                                const tagLink = typeof tag === "object" ? tag.link : null;
                                if (!tagName) return null;
                                
                                if (tagLink) {
                                  return (
                                    <Link
                                      key={idx}
                                      href={tagLink}
                                      target="_blank"
                                      sx={{
                                        textDecoration: "none",
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: "6px",
                                        bgcolor: colors.primary[10],
                                        border: `1px solid ${colors.primary[100]}`,
                                        fontSize: 12,
                                        color: colors.primary[300],
                                        "&:hover": { bgcolor: colors.primary[20] },
                                      }}
                                    >
                                      {tagName} ↗
                                    </Link>
                                  );
                                }
                                return (
                                  <Box key={idx} sx={{ px: 1.5, py: 0.5, borderRadius: "6px", bgcolor: colors.primary[10], border: `1px solid ${colors.primary[100]}` }}>
                                    <Typography sx={ { fontSize: 12, color: colors.primary[300] }}>{tagName}</Typography>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Stack>
                        )}

                        {/* Status Controls */}
                        <Stack direction="row" spacing={3} alignItems="center" sx={{ px: 4, py: 2, borderTop: `1px solid ${colors.neutral[100]}`, bgcolor: "#fff" }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={ { fontSize: 12, color: colors.neutral[600] }}>Status:</Typography>
                            <RadioGroup
                              row
                              value={currentStatus}
                              onChange={handleStatusChange}
                              sx={{ "& .MuiFormControlLabel-root": { marginRight: 2 } }}
                            >
                              <MuiFormControlLabel
                                value="OPEN"
                                control={<Radio size="small" disabled={updatingStatus} sx={{ color: colors.primary[300], "&.Mui-checked": { color: colors.primary[300] }, "&.Mui-disabled": { color: colors.neutral[400] } }} />}
                                label="Open"
                                sx={{ "& .MuiFormControlLabel-label":  { fontSize: 12, cursor: updatingStatus ? "not-allowed" : "pointer" }, cursor: updatingStatus ? "not-allowed" : "pointer" }}
                              />
                              <MuiFormControlLabel
                                value="CLOSED"
                                control={<Radio size="small" disabled={updatingStatus} sx={{ color: colors.primary[300], "&.Mui-checked": { color: colors.primary[300] }, "&.Mui-disabled": { color: colors.neutral[400] } }} />}
                                label="Closed"
                                sx={{ "& .MuiFormControlLabel-label":  { fontSize: 12, cursor: updatingStatus ? "not-allowed" : "pointer" }, cursor: updatingStatus ? "not-allowed" : "pointer" }}
                              />
                            </RadioGroup>
                            {updatingStatus && <CircularProgress size={16} />}
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })()}

                {/* Accept Confirmation Accordion */}
                {acceptAccordionOpen && !isAccepted && (
                  <Box sx={{ px: 4, pb: 2 }}>
                    <Accordion
                      expanded={acceptAccordionOpen}
                      sx={{
                        boxShadow: "none",
                        border: `1px solid ${colors.neutral[200]}`,
                        borderRadius: "8px",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore sx={{ color: colors.neutral[600] }} />}
                        sx={{
                          px: 3,
                          py: 2,
                          "& .MuiAccordionSummary-content": {
                            my: 0,
                          },
                        }}
                      >
                        <Typography sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
                          Confirm Acceptance
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 3 }}>
                        <Stack spacing={3}>
                          <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                            Please provide a reason for accepting this risk. This will help track why this vulnerability was accepted.
                          </Typography>
                          <SafeTextField
                            multiline
                            rows={4}
                            placeholder="Enter reason for acceptance..."
                            value={acceptReason}
                            onChange={(e) => setAcceptReason(e.target.value)}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: 14,
                                "& fieldset": {
                                  borderColor: colors.neutral[200],
                                },
                                "&:hover fieldset": {
                                  borderColor: colors.neutral[300],
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary[300],
                                },
                              },
                            }}
                          />
                          <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setAcceptAccordionOpen(false);
                                setAcceptReason("");
                              }}
                              disabled={acceptingRisk}
                              sx={{
                                textTransform: "none",
                                fontSize: 14,
                                color: colors.neutral[700],
                                borderColor: colors.neutral[300],
                                borderRadius: "8px",
                                px: 3,
                                py: 1,
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleConfirmAccept}
                              disabled={acceptingRisk || !acceptReason.trim()}
                              sx={{
                                textTransform: "none",
                                fontSize: 14,
                                bgcolor: colors.primary[300],
                                borderRadius: "8px",
                                px: 3,
                                py: 1,
                                "&:hover": {
                                  bgcolor: colors.primary[400],
                                },
                                "&:disabled": {
                                  bgcolor: colors.neutral[300],
                                  color: colors.neutral[500],
                                },
                              }}
                            >
                              {acceptingRisk ? "Accepting..." : "Confirm Accept"}
                            </Button>
                          </Stack>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}

                {/* Content */}
                <Box sx={{ p: 4 }}>
                  {riskDetailsLoading ? (
                    <Stack spacing={2}>
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={60} />
                      ))}
                    </Stack>
                  ) : !riskDetails ? (
                    <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>No details available</Typography>
                  ) : (
                    <>
                      {/* Description Section */}
                      <Stack spacing={2} sx={{ mb: 4 }}>
                        <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
                          Description
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: colors.neutral[800],
                            lineHeight: "24px",
                            "& p": { margin: 0, marginBottom: "12px" },
                            "& p:last-child": { marginBottom: 0 },
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              riskDetails.riskDefinition?.description ||
                              riskDetails.description ||
                              riskDetails.risk_definition?.description ||
                              "No description available.",
                          }}
                        />
                      </Stack>

                      {/* Solution Section */}
                      <Stack spacing={2} sx={{ mb: 4 }}>
                        <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
                          Solution
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: colors.neutral[800],
                            lineHeight: "24px",
                            "& p": { margin: 0, marginBottom: "12px" },
                            "& p:last-child": { marginBottom: 0 },
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              riskDetails.riskDefinition?.solution ||
                              riskDetails.solution ||
                              riskDetails.risk_definition?.solution ||
                              "No solution provided.",
                          }}
                        />
                      </Stack>

                      {/* Tabs */}
                      {riskDetails &&
                        (() => {
                          const { instances, references } = normalizeFindingEvidence(riskDetails);

                          return (
                            <Box sx={{ borderBottom: `1px solid ${colors.neutral[200]}`, mb: 3, px: 2 }}>
                              <Tabs
                                value={drawerTab}
                                onChange={(e, newValue) => setDrawerTab(newValue)}
                                sx={{
                                  "& .MuiTab-root": {
                                    fontSize: 14,
                                    textTransform: "none",
                                    minHeight: 48,
                                    px: 3,
                                    py: 1.5,
                                    mr: 2,
                                  },
                                  "& .Mui-selected": {
                                    color: colors.primary[300],
                                    fontWeight: "medium",
                                  },
                                  "& .MuiTabs-indicator": {
                                    backgroundColor: colors.primary[300],
                                    height: 2,
                                  },
                                }}
                              >
                                <Tab label={`Instances (${instances.length})`} />
                                <Tab label={`References (${references.length})`} />
                              </Tabs>
                            </Box>
                          );
                        })()}

                      {/* Tab Content */}
                      {drawerTab === 0 &&
                        (() => {
                          const { instances } = normalizeFindingEvidence(riskDetails);

                          const SectionTitle = ({ title }) => (
                            <Typography sx={ { fontSize: 13, fontWeight: "medium", color: colors.neutral[800] }}>{title}</Typography>
                          );

                          const CodeBlock = ({ title, content, maxHeight = "200px" }) => (
                            <Stack spacing={1.5} sx={{ mb: 3 }}>
                              <SectionTitle title={title} />
                              <Box
                                component="pre"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: 11,
                                  color: colors.neutral[800],
                                  bgcolor: "#fff",
                                  p: 2,
                                  borderRadius: "6px",
                                  border: `1px solid ${colors.neutral[200]}`,
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  maxHeight,
                                  margin: 0,
                                }}
                              >
                                {content}
                              </Box>
                            </Stack>
                          );

                          return (
                            <Stack spacing={3} sx={{ mt: 3 }}>
                              {instances.length > 0 ? (
                                instances.map((instance, idx) => (
                                  <Box key={idx} sx={{ p: 3, bgcolor: colors.neutral[50], borderRadius: "8px", border: `1px solid ${colors.neutral[100]}` }}>
                                    {/* URI and Method Row */}
                                    <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ mb: 3 }}>
                                      {instance.uri && (
                                        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                          <SectionTitle title="URI" />
                                          <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.primary[300], wordBreak: "break-all" }}>{instance.uri}</Typography>
                                        </Stack>
                                      )}
                                      {instance.method && (
                                        <Stack spacing={1}>
                                          <SectionTitle title="Method" />
                                          <Box sx={{ px: 2, py: 0.5, borderRadius: "4px", bgcolor: colors.primary[10], border: `1px solid ${colors.primary[100]}` }}>
                                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, fontWeight: "medium", color: colors.primary[300] }}>{instance.method}</Typography>
                                          </Box>
                                        </Stack>
                                      )}
                                    </Stack>

                                    {/* Param */}
                                    {instance.param && (
                                      <Stack spacing={1} sx={{ mb: 3 }}>
                                        <SectionTitle title="Parameter" />
                                        <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.neutral[800], bgcolor: "#fff", px: 2, py: 1, borderRadius: "4px", border: `1px solid ${colors.neutral[200]}` }}>{instance.param}</Typography>
                                      </Stack>
                                    )}

                                    {/* Evidence */}
                                    {instance.evidence && (
                                      <Stack spacing={1} sx={{ mb: 3 }}>
                                        <SectionTitle title="Evidence" />
                                        <Box sx={{ p: 2, bgcolor: colors.yellow[10], borderRadius: "6px", border: `1px solid ${colors.yellow[100]}` }}>
                                          <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.neutral[800], wordBreak: "break-all" }}>{instance.evidence}</Typography>
                                        </Box>
                                      </Stack>
                                    )}

                                    {/* Other Info */}
                                    {instance.otherinfo && (
                                      <Stack spacing={1} sx={{ mb: 3 }}>
                                        <SectionTitle title="Additional Information" />
                                        <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: "6px", border: `1px solid ${colors.neutral[200]}` }}>
                                          <Typography sx={ { fontSize: 13, color: colors.neutral[700], lineHeight: 1.6 }}>{instance.otherinfo}</Typography>
                                        </Box>
                                      </Stack>
                                    )}

                                    {/* Request Header */}
                                    {instance.requestHeader && <CodeBlock title="Request Headers" content={instance.requestHeader} />}

                                    {/* Response Header */}
                                    {instance.responseHeader && <CodeBlock title="Response Headers" content={instance.responseHeader} />}

                                    {/* Response Body */}
                                    {instance.responseBody && <CodeBlock title="Response Body" content={instance.responseBody} maxHeight="300px" />}
                                  </Box>
                                ))
                              ) : (
                                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                                  <Typography sx={ { fontSize: 14, color: colors.neutral[500] }}>No instances available</Typography>
                                </Stack>
                              )}
                            </Stack>
                          );
                        })()}

                      {drawerTab === 1 &&
                        (() => {
                          const { references } = normalizeFindingEvidence(riskDetails);

                          return (
                            <Stack spacing={2} sx={{ mt: 3 }}>
                              {references.length > 0 ? (
                                <Stack spacing={2}>
                                  {references.map((ref, idx) => (
                                    <Link
                                      key={idx}
                                      href={typeof ref === "string" ? ref : ref.url || ref}
                                      target="_blank"
                                      sx={{
                                        fontSize: 14,
                                        color: colors.primary[300],
                                        textDecoration: "none",
                                        "&:hover": { textDecoration: "underline" },
                                        display: "block",
                                      }}
                                    >
                                      {typeof ref === "string" ? ref : ref.title || ref.url} →
                                    </Link>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                                  No references available
                                </Typography>
                              )}
                            </Stack>
                          );
                        })()}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Slide>
      </Modal>
      )}
    </>
  );
};

export default NewFindingsPage;
